import { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import Konva from 'konva';
import { Stage, Layer, Rect, Circle, Sprite, Group, Image as KonvaImage, Line, Text } from 'react-konva';
import { memo } from "react";
import { QuestionnaireContext } from "../../../StateManagment/Questionnaire";
import tintColor from "../../../helpers/questionnaire/tintColor";
import hectometerLabel from "../../../helpers/questionnaire/hectometerLabel";

const hillSectionSize = { x: 480, y: 1080 }; // Size of the hill section
const landMarkSize = {x: 256, y: 256 }; // Size of the landmark image

const hectometerSize = {
    x: 100,
    y: 30
}

const hills = [
    '/canvas/hills/asset_hilltop_v1_fig.png',
    '/canvas/hills/asset_hilltop_v2_fig.png',
];

const hillBackgrounds = [
    '/canvas/hills/asset_hilltop_v1_fig_background.png',
    '/canvas/hills/asset_hilltop_v2_fig_background.png',
];

const hillForegrounds = [
    '/canvas/hills/asset_hilltop_v2_fig_foreground.png',
    '/canvas/hills/asset_hilltop_v1_fig_foreground.png',
];

const landMarks = [
    '/canvas/landmark/Landmark_Church_minimal.png',
    '/canvas/landmark/Landmark_Farm.png',
    '/canvas/landmark/Landmark_Houses.png',
    '/canvas/landmark/Landmark_Tulipfield.png',
    '/canvas/landmark/Landmark_Windmill_spritesheet.png',
];

const playerImg = new Image();
playerImg.src = '/canvas/cyclist/cyclist_sprite_sheet.png';

const playerSpriteSize = 256;
const playerFrames = {
    cycle: [ // Row 0
        playerSpriteSize*0, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*1, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*2, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*3, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*4, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*5, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*6, 0, playerSpriteSize, playerSpriteSize,
        playerSpriteSize*7, 0, playerSpriteSize, playerSpriteSize,
    ],
    idle: [ // Row 1
        playerSpriteSize*0, playerSpriteSize*1, playerSpriteSize, playerSpriteSize,
    ],
};

const playerSize = { x: 150, y: 150 }; // Size of the player image

/* End animation */
const postBoxImage = new Image();
postBoxImage.src = '/canvas/cyclist/postbox_cyclist_aligned.png';

const endAnimationSpriteSheet = new Image();
endAnimationSpriteSheet.src = '/canvas/cyclist/cyclist_postbox_spritesheet.png';
const cyclistSpriteSize = 256; // Height of each frame in the sprite sheet
const endAnimationFrames = {
    anim0: [
        cyclistSpriteSize*0, 0, cyclistSpriteSize, cyclistSpriteSize,
        cyclistSpriteSize*1, 0, cyclistSpriteSize, cyclistSpriteSize,
        cyclistSpriteSize*2, 0, cyclistSpriteSize, cyclistSpriteSize,
        cyclistSpriteSize*3, 0, cyclistSpriteSize, cyclistSpriteSize,
        cyclistSpriteSize*4, 0, cyclistSpriteSize, cyclistSpriteSize,
        cyclistSpriteSize*5, 0, cyclistSpriteSize, cyclistSpriteSize,
        cyclistSpriteSize*6, 0, cyclistSpriteSize, cyclistSpriteSize,
        cyclistSpriteSize*7, 0, cyclistSpriteSize, cyclistSpriteSize,
        cyclistSpriteSize*8, 0, cyclistSpriteSize, cyclistSpriteSize,
        cyclistSpriteSize*9, 0, cyclistSpriteSize, cyclistSpriteSize,
        cyclistSpriteSize*10, 0, cyclistSpriteSize, cyclistSpriteSize,
        cyclistSpriteSize*11, 0, cyclistSpriteSize, cyclistSpriteSize,
    ],
};

/**
 * @typedef {Object} Point
 * @property {number} x - The x-coordinate of the point.
 * @property {number} y - The y-coordinate of the point.
 */

/**
 * @typedef {Object} MapSection
 * @property {HTMLImageElement} image - The hill image.
 * @property {HTMLImageElement} background - The background image of the hill.
 * @property {HTMLImageElement} foreground - The foreground image of the hill.
 * @property {HTMLImageElement} landmark - The landmark image.
 * @property {boolean} landmarkIsSprite - Whether the landmark image is a sprite sheet.
 * @property {number} width - The width of the section.
 * @property {number} height - The height of the section.
 * @property {Point} point - The anchor point of the section.
 * @property {Point[]} pathPoints - The points that define the path of the section.
 * @property {Object} responsive - Responsive data for the section.
 * @property {Point} responsive.point - Responsive anchor point.
 * @property {Point[]} responsive.pathPoints - Responsive path points.
 */

/**
 * @param {number} sectionCount - The number of sections to generate.
 * @param {number} padStart - Extra sections at the start of the map.
 * @param {number} padEnd - Extra sections at the end of the map.
 * @return {MapSection[]}
 */
const generateMap = (sectionCount, padStart, padEnd) => {

    const mapSections = [];

    const finishImage = new Image();
    finishImage.src = '/canvas/landmark/Landmark_Postbox.png';

    for (let i = 0; i < sectionCount + (padStart || 0) + (padEnd || 0); i++) {

        const base = new Image();
        // base.src = hills[Math.floor(Math.random() * hills.length)];
        base.src = hills[i % hills.length];

        const fg = new Image();
        // fg.src = hillForegrounds[Math.floor(Math.random() * hillForegrounds.length)];
        fg.src = hillForegrounds[i % hillForegrounds.length];

        const bg = new Image();
        // bg.src = hillBackgrounds[Math.floor(Math.random() * hillBackgrounds.length)];
        bg.src = hillBackgrounds[i % hillBackgrounds.length];

        const landMarkImage = new Image();
        // landMarkImage.src = landMarks[Math.floor(Math.random() * landMarks.length)];
        landMarkImage.src = landMarks[i % landMarks.length];

        if (i >= (padStart + sectionCount)) {
            // Last section, use the postbox image
            landMarkImage.src = finishImage.src;
        }

        /**
         * @type {MapSection}
         */
        const data = {
            image: base,
            background: bg,
            foreground: fg,
            landmark: landMarkImage,
            landmarkIsSprite: landMarkImage.src.includes('spritesheet'),
            width: hillSectionSize.x,
            // height: hillSectionSize.y,
            height: window.innerHeight, // Use the full height of the window
            point: { x: 0, y: 0 },
            pathPoints: [],
            responsive: {
                point: { x: 0, y: 0 },
                pathPoints: []
            }
        }

        const yBase = 0.63; // tune this later
        
        // Get the anchor from the image name - formatted as `asset_hilltop_section_v1_240_1440`
        // Where the last two numbers are the X and Y anchor points.
        // let match = false; // base.src.match(/_([0-9]+)\.png$/i);

        // if (match) {
        //     data.point = {
        //         x: 0.5,
        //         y: parseInt(match[1]) / 100
        //     }
        // } else {
        //     // console.warn(`Could not extract anchor point from image source: ${base.src}`);
        //     data.point = {
        //         x: 0.5,
        //         y: 0.5 
        //     }
        // }

        data.point = {
            x: 0.47, // Slightly left of center
            y: 0.6 
        }
            
        data.pathPoints = [
            { x: 0, y: yBase }, // Start point at the left side of the section
            data.point,         // Middle point at the middle of the section
            { x: 1, y: yBase }, // End point at the right side of the section
        ];
        mapSections.push(data);
    }

    return mapSections;
};

/* Some state outside React to prevent re-renders from updating the animation */
const animationState = {
    /** Player path follow 't' parameter */
    playerPathT: 0,

    /** Current X position of the map scrolling animation */
    mapX: 0,

    /** Camera position */
    camera: { 
        /** Camera X offset */
        x: 0, 
        /** Camera Y offset */
        y: 0, 
        /** Scale / zoom */
        zoom: 1 
    },
}

const BASE_SKY = '#BED2D9';

/**
 * @param {Object} props
 * @param {number} props.current - The current index in the questionnaire (zero-based).
 * @param {number} props.total - Total number of questions
 * @param {string} [props.sectionTitle] - Title of the current section, shown in the sky.
 * @param {string} [props.sectionColor] - Colour of the current section, lightly tints the sky.
 * @param {string} [props.stepLabel] - Waar de invuller is ('Vraag 1 / 3'), in de lucht; leeg = niets tonen.
 */
const ImmersiveProgressBar = (props) => {

    const skyColor = props.sectionColor ? tintColor(BASE_SKY, props.sectionColor, 0.22) : BASE_SKY;

    const { isTransitioning } = useContext(QuestionnaireContext);

    /** @type {React.Ref<HTMLDivElement>} */
    const containerRef = useRef(null);
    
    /** @type {React.Ref<Konva.Group>} */
    const mapRef = useRef(null);
    
    /** @type {React.Ref<Konva.Sprite>} */
    const playerRef = useRef(null);

    /** @type {React.Ref<Konva.Sprite>} */
    const endAnimationRef = useRef(null);
    
    /** @type {React.Ref<Konva.Image>} */
    const postBoxRef = useRef(null);

    /** Container dimensions. */ 
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    /** Visible sections based on screen size. */ 
    const [visibleSections, setVisibleSections] = useState(4);

    const padStart = 2;
    const padEnd = 4; // Should always be enough to show the max of `visibleSections` + 1

    const mapSections = useMemo(() => {
        return generateMap(props.total, padStart, padEnd)
    }, [props.total, padStart, padEnd]);

    const endReached = props.current >= props.total;

    const currentSectionIndex = props.current + padStart; // Adjust for padding
    const currentMapSection = mapSections[currentSectionIndex];

    const w = dimensions.width;
    const h = dimensions.height;

    const sectionWidth = w / visibleSections;
    // const sectionHeight = h * 1.0; // Use the full height of the window
    const sectionHeight = Math.min(hillSectionSize.y, h); // Scale height to fit, but never exceed hillSectionSize.y
    
    /* Convert all section data to pixel coordinates based on screen size */
    // useMemo(() => {
        mapSections.forEach((section, i) => {
            /**
             * @param {Point} point 
             * @returns {Point}
             */
            const toPixelCoords = (point) => ({
                x: (point.x * sectionWidth + i * sectionWidth),
                y: point.y * sectionHeight
            });

            section.responsive = {
                pathPoints: section.pathPoints.map(toPixelCoords),
                point: toPixelCoords(section.point)
            };
        });
    // }, [sectionWidth, sectionHeight, mapSections, w]);

    useEffect(() => {
        const resize = () => {
            if (containerRef.current) {
                const { clientWidth, clientHeight } = containerRef.current;
                setDimensions({ width: clientWidth, height: clientHeight });
            }
        };

        resize(); // Initial size
        window.addEventListener("resize", resize);

        return () => window.removeEventListener("resize", resize);
    }, [containerRef]);
    
    /* Process hill data to create pixel coordinates */
    useEffect(() => {
        if (w <= 0 || h <= 0) return;

        if (w >= 1440) {
            setVisibleSections(4);
        } else if (w >= 1280) {
            setVisibleSections(3);
        } else if (w >= 960) {
            setVisibleSections(2);
        } else {
            setVisibleSections(1);
        }

    }, [w, h]);

    const renderMap = () => {
        const sections = [];

        const startX = -padStart * sectionWidth;

        for (let i = 0; i < mapSections.length; i++) {
            
            if (i < currentSectionIndex - padStart || i > currentSectionIndex + visibleSections) {
                continue; // Skip sections outside the visible range
            }
            
            // const endReached = i >= (mapSections.length - padEnd);

            const mapSection = mapSections[i];
            let landmark = mapSection.landmark;

            if (i > (mapSections.length - padEnd - 1)) {
                landmark = null;
            }
            // Show hectometer every 'N visibleSections', but not at the end.
            const showHectometer = (i % visibleSections === 0 && i !== 0) &&
                i < (mapSections.length - padEnd);

            sections.push(
                <Group key={i} 
                    name={`map-section-${i}`}
                    x={startX + (i * sectionWidth)} 
                    y={sectionHeight}
                    offset={{
                        x: 0,
                        y: sectionHeight
                    }}
                    >
                    {/* Debugging  */}
                    {/* <Rect
                        name="section-debug"
                        x={0}
                        y={0}
                        width={sectionWidth}
                        height={sectionHeight}
                        fill="#ff00ff"
                        stroke={"black"}
                    /> */}
                    {/* <Circle
                        x={mapSection.point.x * sectionWidth}
                        y={mapSection.point.y * sectionHeight}
                        radius={10}
                        fill="red"
                    /> */}
                    <KonvaImage
                        name="hill-background"
                        image={mapSection.background}
                        // x={i * sectionWidth}
                        width={sectionWidth}
                        height={sectionHeight}
                        scaleX={1.01} // Slightly scale to avoid gaps
                    />
                    {mapSection.landmarkIsSprite ? (
                        <Sprite
                            name="landmark-sprite"
                            offset={{ 
                                x: landMarkSize.x/2, 
                                y: landMarkSize.y - landMarkSize.y * 0.11
                            }}
                            image={landmark}
                            x={mapSection.point.x * sectionWidth}
                            y={mapSection.point.y * sectionHeight}
                            width={landMarkSize.x}
                            height={landMarkSize.y}
                            animation="anim0"
                            animations={{
                                anim0: [
                                    landMarkSize.x * 0, 0, landMarkSize.x, landMarkSize.y,
                                    landMarkSize.x * 1, 0, landMarkSize.x, landMarkSize.y,
                                    landMarkSize.x * 2, 0, landMarkSize.x, landMarkSize.y,
                                    landMarkSize.x * 3, 0, landMarkSize.x, landMarkSize.y,
                                ]
                            }}
                            frameRate={4}
                            frameIndex={0}
                            ref={(node) => {
                                if (node) {
                                    node.start(); // Start the sprite animation
                                }
                            }}
                        />
                    ) : (
                        landmark && <KonvaImage
                            name="landmark"
                            offset={{ 
                                x: landMarkSize.x/2, 
                                y: landMarkSize.y - landMarkSize.y * 0.11
                                // x: landmark.width / 2,
                                // y: landmark.height - landmark.height * 0.1
                            }}
                            image={landmark} 
                            x={mapSection.point.x * sectionWidth}
                            y={mapSection.point.y * sectionHeight}
                            width={landMarkSize.x}
                            height={landMarkSize.y}
                        />
                    )}
                    <KonvaImage
                        name="hill-base"
                        image={mapSection.image}
                        width={sectionWidth}
                        height={sectionHeight}
                        scaleX={1.01} // Slightly scale to avoid gaps
                    />
                    {showHectometer && <Group
                        name="hectometer-paaltje"
                        x={sectionWidth/2 + 20}
                        y={mapSection.responsive.point.y - 30}
                        // offsetX={100/2}
                        >
                        <Rect 
                            name="paaltje"
                            x={hectometerSize.x/2}
                            y={0}
                            offsetX={10/2}
                            width={10}
                            height={60}
                            fill="#5c4531"
                        />
                        <Rect
                            name="achtergrond"
                            width={hectometerSize.x}
                            height={hectometerSize.y}
                            fill="#3a8a52"
                            cornerRadius={0}
                            shadowColor="rgba(0, 0, 0, 0.5)"
                            shadowBlur={5}
                            shadowOffsetX={2}
                            shadowOffsetY={2}
                        />
                        <Rect
                            name="wit-randje"
                            x={hectometerSize.x/2}
                            y={hectometerSize.y/2}
                            offsetX={(hectometerSize.x-5)/2}
                            offsetY={(hectometerSize.y-5)/2}
                            width={(hectometerSize.x-5)}
                            height={(hectometerSize.y-5)}
                            stroke={"white"}
                            strokeWidth={1.55}
                            cornerRadius={0}
                            shadowColor="rgba(0, 0, 0, 0.5)"
                            shadowBlur={5}
                            shadowOffsetX={2}
                            shadowOffsetY={2}
                        />
                        <Text
                            name="hectometer-text"
                            text={hectometerLabel(i - padStart, props.total)}
                            fontSize={15}
                            width={hectometerSize.x*0.95}
                            height={hectometerSize.y+2}
                            fill="white"
                            fontFamily="Arial"
                            fontStyle="bold"
                            align="center"
                            verticalAlign="middle"
                            // padding={10}
                            shadowColor="rgba(0, 0, 0, 0.5)"
                            shadowBlur={3}
                            shadowOffsetX={2}
                            shadowOffsetY={2}
                        />
                    </Group>}
                    <KonvaImage
                        name="hill-foreground"
                        image={mapSection.foreground}
                        width={sectionWidth}
                        height={sectionHeight}
                        scaleX={1.01} // Slightly scale to avoid gaps
                    />
                    {/* <Line
                        name="hill-path"
                        points={mapSection.responsive.pathPoints.map(p => {
                            return [
                                p.x + i * sectionWidth, 
                                p.y
                            ]
                        }).flat()}
                        stroke="blue"
                        strokeWidth={15}
                        lineCap="round"
                        lineJoin="round"
                        tension={0}
                    /> */}
                </Group>
            );
        }
        return sections;
    };

    /* Add points to positions, except for the last point, 
        which is the next section's start point */
    /* Note: useMemo was causing issues with responsiveness / devicePixelRatio */
    // const positionsAsSplineFormat = useMemo(() => {
        // if (w <= 0) return [];
        
        const selectedPoints = mapSections
            // .slice(0, props.total + padEnd) // Continue until 'end' of screen
            .slice(0, props.total + 1) // Stop at 'first final section'

        /* Remove first path point from selected points */
        selectedPoints[0].responsive.pathPoints.shift();
            
        /* Convert to Konva.js format */
        const points = selectedPoints.flatMap(section => 
                section.responsive.pathPoints
                    .slice(0, -1) // Exclude the last point to avoid overlap
                    .flatMap(p => [p.x, p.y])
            );

        // return points;
        const positionsAsSplineFormat = points;
    // }, [mapSections, props.total, w]);

    useEffect(() => {
        if (!playerRef.current) return;
        if (w <= 0 || h <= 0) return;

        const obj = playerRef.current;

        // Note: visibleSections can be [0 - 4] based on screen size. 
        // On large screens, it can be 4, on small screens it can be 1.
        // This means that the total number of sections is variable.
        // props.total, padStart, padEnd
        // const totalSections = props.total;
        // const totalProgress = props.current / totalSections;

        const totalProgress = props.current / (props.total); // Normalize to [0, 1]

        const anim = new Konva.Animation((frame) => {
            if (!frame) return;
            const t = frame.time / 2000;
            
            // lerp currentT to totalProgress
            const delta = totalProgress - animationState.playerPathT;
            const step = delta * t;
            const lerp = endReached ? 0.03 : 0.01; // A bit quicker on the end
            animationState.playerPathT += delta * lerp; // Smooth transition to target progress
            
            const speed = Math.abs(step);
            obj.frameRate(speed < 0.1 ? speed < 0.01 ? 3 : 5 : 8);

            if (speed < 0.002) {
                obj.animation('idle'); // Switch to cycling animation
            } else {
                obj.animation('cycle'); // Switch to cycling animation
            }

            const pos1 = getPointOnSpline(positionsAsSplineFormat, animationState.playerPathT);
            const pos2 = getPointOnSpline(positionsAsSplineFormat, animationState.playerPathT + 0.003);

            const dx = pos2.x - pos1.x;
            const dy = pos2.y - pos1.y;
            const angle = Math.atan2(dy, dx); // radians

            if (pos1.x === Infinity || pos1.x === -Infinity) {
                pos1.x = 0;
            }

            obj.position(pos1);
            obj.rotation(animationState.playerPathT < 0.001
                ? 0 // Don't rotate at the start
                : (angle * 180) / Math.PI);
                
            obj.scale({
                x: delta < 0 
                    ? -1 * (playerSize.x / playerSpriteSize) // Reverse / flip
                    : playerSize.x / playerSpriteSize,
                y: playerSize.y / playerSpriteSize
            });
        }, obj.getLayer());

        obj.start();

        anim.start();
        return () => {anim.stop()};
    }, [props, positionsAsSplineFormat, w, h, playerRef, endReached]);

    useEffect(() => {
        if (!endAnimationRef.current) return;
        if (w <= 0 || h <= 0) return;

        const endAnim = endAnimationRef.current;
        const postbox = postBoxRef.current;
        const player = playerRef.current;

        if (!endAnim || !postbox || !player) return;

        // get final point on player path 
        const finalPos = getPointOnSpline(positionsAsSplineFormat, 1.0);

        // Move the end animation to the final position
        endAnim.x(finalPos.x);
        endAnim.y(finalPos.y);
        postbox.x(finalPos.x);
        postbox.y(finalPos.y);

        postbox.visible(true);

        // sprite.start();
        /* Sprite animation that ends on the last frame */
        const animSprite = new Konva.Animation((frame) => {
            if (!endAnim) return;
            const time = frame.time * 0.001; // Convert to seconds

            // Stop at the last frame
            const frameIndex = Math.min(
                Math.floor(time * endAnim.attrs.frameRate),
                11 // Last frame index
            );

            endAnim.frameIndex(frameIndex);
        });

        /* Animation that moves the player to the end position */
        const endAnimHandler = new Konva.Animation((frame) => {
            if (!endAnim) return;

            const diff = (finalPos.x - player.x());

            if (diff < 1) {
                player.visible(false);
                endAnim.visible(true);
                
                endAnim.animation('anim0');
                endAnim.frameIndex(0);

                endAnimHandler.stop();
                animSprite.start();
            }
        }, endAnim.getLayer());

        let shouldReset = true;
        // const lastTransition = (props.current >= props.total - 1)

        if (isTransitioning) {
            shouldReset = false;

            /* Hide cyclist behind the left side of the screen */
            // animationState.camera = {
            //     x: visibleSections === 1 
            //         ? sectionWidth 
            //         : sectionWidth/2,
            //     y: 0,
            //     zoom: 1.5
            // }
        }

        if (endReached) {
            shouldReset = false;
            endAnimHandler.start();

            animationState.camera = {
                x: -w/2 + sectionWidth/2, // Zoom to center of section
                y: window.innerWidth < 2560 
                    ? -50
                    : 0,
                zoom: 2.5 * (Math.min(w, 720) / 720)
            }
        }
        
        if (shouldReset) {
            player.visible(true);
            endAnim.visible(false);
            endAnimHandler.stop();

            animationState.camera = {
                x: 0,
                y: 0,
                zoom: 1.0
            }
        }

        return () => {
            endAnim.stop();
            animSprite.stop();
            endAnimHandler.stop();
        }

    }, [endReached, w, h, endAnimationRef, positionsAsSplineFormat, sectionWidth, isTransitioning]);

    /* Scroll the map to the current section */
    useEffect(() => {
        if (!mapRef.current) return;
        if (w <= 0 || h <= 0) return;

        const map = mapRef.current;

        let t = 0;
        let targetX = -(currentSectionIndex - padStart) * sectionWidth;
        
        const anim = new Konva.Animation((frame) => {
            if (!frame) return;
            t = frame.time / 10_000;

            /* lerp to targetX */
            const dx = targetX - animationState.mapX;
            if (Math.abs(dx) > 0.001) { /* Avoid small floating-point errors */
                const step = dx * t;
                animationState.mapX = animationState.mapX + step;
            } else {
                animationState.mapX = targetX; /* Snap to target when close enough */
            }

            map.setPosition({
                x: animationState.mapX,
                y: map.y(), //h
            });

            /* Handle scaling */
            const zoomLerp = t;
            const l = map.getLayer();

            // l.x(l.x() + ((w / 2 + animationState.camera.x) - l.x()) * zoomLerp);
            // l.y(l.y() + ((h / 2 + animationState.camera.y) - l.y()) * zoomLerp);
            
            // const newScale = l.scaleX() + (animationState.camera.zoom - l.scaleX()) * zoomLerp;

            // Calculate camera offset based on zoom level
            const zoom = animationState.camera.zoom;

            const currentX = l.x();
            const currentY = l.y();
            const lX = w / 2 - animationState.camera.x * l.scaleX();
            const lY = h / 2 - animationState.camera.y * l.scaleX();

            l.x(currentX + (lX - currentX) * zoomLerp);
            l.y(currentY + (lY - currentY) * zoomLerp);

            // l.x(w / 2 - animationState.camera.x * zoom);
            // l.y(h / 2 - animationState.camera.y * zoom);

            const newScale = l.scaleX() + 
                (zoom - l.scaleX()) * zoomLerp;

            l.scale({
                x: newScale,
                y: newScale
            }); 

            /* Handle glitches with Large/Infinity values */
            if (l.x() > 10_000 || !isFinite(l.x())) l.x(animationState.camera.x);
            if (l.y() > 10_000 || !isFinite(l.y())) l.y(animationState.camera.y);
            if (l.scaleX() > 10_000 || !isFinite(l.scaleX())) l.scale({ x: animationState.camera.zoom, y: animationState.camera.zoom });

        }, map.getLayer());

        anim.start();
        return () => {anim.stop()};
    }, [mapRef, currentSectionIndex, currentMapSection, sectionWidth, w, h]);

    return (
        <div ref={containerRef} style={{
            pointerEvents: 'none', position: 'fixed',
            top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: skyColor,
            transition: 'background-color 1.2s ease',
            // zIndex: 0,
            // zIndex: 100, // Debug: on top
            }}>
            <div className="immersive-sky-heading">
                {/* De teller; het paaltje langs de weg staat op een vaste plek en
                    is geen teller (testronde 2026-10-15, B7). */}
                <div className="immersive-sky-step" aria-live="polite">
                    {props.stepLabel}
                </div>
                <div
                    className="immersive-sky-section-title"
                    style={{ opacity: props.sectionTitle ? 1 : 0 }}
                >
                    {props.sectionTitle}
                </div>
            </div>
            <Stage width={dimensions.width} height={dimensions.height}>
                <Layer 
                    /* Center the layer */
                    x={w/2} 
                    y={h/2}
                    offset={{
                        x: w/2,
                        y: h/2
                    }}
                    >
                    <Group name="map"
                        ref={mapRef}
                        offset={{ 
                            x: 0, 
                            y: sectionHeight
                        }}
                        x={0}
                        y={window.innerWidth < 390
                            ? h * 0.56
                            : window.innerWidth < 720 
                                ? h * 0.65
                                : h * 0.75
                        }
                        >

                        <Rect 
                            name="bottom-extender"
                            x={0}
                            y={sectionHeight*0.99}
                            width={sectionWidth * mapSections.length}
                            height={h}
                            fill="#6E7032"
                        />
                            
                        {renderMap()}

                        <Group name='end-animation'></Group>
                        {renderMap()}

                        <Group name='end-animation'>
                            <KonvaImage
                                visible={false}
                                ref={postBoxRef}
                                x={0}
                                y={0}
                                image={postBoxImage}
                                offsetX={playerSize.x/2}
                                offsetY={playerSize.y/2}
                                scale={{
                                    x: playerSize.x / cyclistSpriteSize, // Scale to fit the sprite sheet
                                    y: playerSize.y / cyclistSpriteSize
                                }}
                                offset={{ 
                                    x: cyclistSpriteSize/2, 
                                    y: cyclistSpriteSize/2
                                }}
                            />
                            <Sprite 
                                ref={endAnimationRef}
                                visible={false}
                                x={w / 2}
                                y={h / 2}
                                image={endAnimationSpriteSheet}
                                offsetX={playerSize.x/2}
                                offsetY={playerSize.y/2}
                                scale={{
                                    x: playerSize.x / cyclistSpriteSize, // Scale to fit the sprite sheet
                                    y: playerSize.y / cyclistSpriteSize
                                }}
                                offset={{ 
                                    x: cyclistSpriteSize/2, 
                                    y: cyclistSpriteSize/2
                                }}
                                animation="anim0"
                                animations={endAnimationFrames}
                                frameRate={8}
                                frameIndex={0}
                            />
                        </Group>

                        <Sprite
                            name="player"
                            ref={playerRef}
                            scale={{
                                x: playerSize.x / playerSpriteSize, // Scale to fit the sprite sheet
                                y: playerSize.y / playerSpriteSize
                            }}
                            offset={{ 
                                x: playerSpriteSize/2, 
                                y: playerSpriteSize/2
                            }}
                            image={playerImg}
                            animation="cycle"
                            animations={playerFrames}
                            frameRate={8}
                            frameIndex={1}
                        />

                        {/* Debug */}
                        {/* <Rect
                            name="padStart"
                            x={-padStart * sectionWidth}
                            y={0}
                            width={padStart * sectionWidth}
                            height={sectionHeight}
                            fill="rgba(255, 0, 255, 0.5)"
                        />
                        <Rect
                            name="main"
                            x={0}
                            y={0}
                            width={props.total * sectionWidth}
                            height={sectionHeight}
                            fill="rgba(255, 255, 0, 0.5)"
                        />
                        <Rect
                            name="padEnd"
                            x={props.total * sectionWidth}
                            y={0}
                            width={padEnd * sectionWidth}
                            height={sectionHeight}
                            fill="rgba(0, 255, 255, 0.5)"
                        /> */}
                        {/* <Line
                            name="progress-line"
                            points={positionsAsSplineFormat}
                            stroke="blue"
                            strokeWidth={5}
                            lineCap="round"
                            lineJoin="round"
                            dash={[10, 10]}
                            tension={0.5}
                            globalCompositeOperation="source-over"
                        /> */}
                    </Group>
                </Layer>
            </Stage>
        </div>
    );
}

export default memo(ImmersiveProgressBar, (prevProps, nextProps) => {
    return prevProps.current === nextProps.current &&
           prevProps.total === nextProps.total &&
           prevProps.sectionTitle === nextProps.sectionTitle &&
           prevProps.stepLabel === nextProps.stepLabel &&
           prevProps.sectionColor === nextProps.sectionColor;
});






// Helpers

// Create a Catmull-Rom spline from the points
const getPointOnSpline = (points, t) => {
  const n = points.length / 2;
  const totalSegments = n - 1;
  const segment = Math.min(Math.floor(t * totalSegments), totalSegments - 1);
  const localT = (t * totalSegments) - segment;

  const p0 = getPoint(points, segment - 1);
  const p1 = getPoint(points, segment);
  const p2 = getPoint(points, segment + 1);
  const p3 = getPoint(points, segment + 2);

  // Catmull-Rom spline formula
  const x = catmullRom(p0.x, p1.x, p2.x, p3.x, localT);
  const y = catmullRom(p0.y, p1.y, p2.y, p3.y, localT);
  return { x, y };
};

const getPoint = (points, i) => {
  const n = points.length / 2;
  const clampIndex = Math.max(0, Math.min(i, n - 1));
  return {
    x: points[clampIndex * 2],
    y: points[clampIndex * 2 + 1]
  };
};

const catmullRom = (p0, p1, p2, p3, t) => {
  return 0.5 * ((2 * p1) + (-p0 + p2) * t +
    (2*p0 - 5*p1 + 4*p2 - p3) * t * t +
    (-p0 + 3*p1 - 3*p2 + p3) * t * t * t);
};

